(function (global) {
  'use strict';

  const CAMPAIGN = 'oclean-bf-2026';
  const LIMIT = 12;
  const integer = value => Math.max(0, Math.round(Number(value) || 0));
  const idOf = value => String(value == null ? '' : value).split('/').pop();
  const escapeHTML = value => String(value == null ? '' : value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const listOf = value => Array.isArray(value) ? value : value && Array.isArray(value.nodes) ? value.nodes : [];
  const unitPrice = item => integer(item.final_price == null ? item.price : item.final_price);
  const linePrice = item => integer(item.final_line_price == null ? item.line_price == null ? unitPrice(item) * integer(item.quantity) : item.line_price : item.final_line_price);
  const propsOf = item => item && item.properties || {};
  const isManaged = (item, campaign) => propsOf(item)._oclean_bf_campaign === campaign;
  const isGift = (item, campaign) => isManaged(item, campaign) && propsOf(item)._oclean_bf_mode === 'gift';

  function refillCalc(items) {
    const packs = items.flatMap(item => Array.from({ length: integer(item.quantity == null ? item.qty == null ? 1 : item.qty : item.quantity) }, () => ({ price: integer(item.price) })));
    const free = Math.floor(packs.length / 3);
    const total = packs.reduce((sum, item) => sum + item.price, 0);
    const discount = packs.map(item => item.price).sort((a, b) => a - b).slice(0, free).reduce((sum, price) => sum + price, 0);
    return { quantity: packs.length, free, total, discount, net: total - discount };
  }

  function managedCounts(cart, campaign) {
    return (cart.items || []).filter(item => isManaged(item, campaign) && !isGift(item, campaign)).reduce((counts, item) => {
      const p = propsOf(item);
      const quantity = integer(item.quantity);
      if (p._oclean_bf_offer === 'single' || p._oclean_bf_offer === 'pair') counts.a10 += quantity * Math.max(1, integer(p._oclean_bf_units));
      if (p._oclean_bf_offer === 'ultra') counts.ultra += quantity;
      if (p._oclean_bf_offer === 'spark') counts.spark += quantity;
      return counts;
    }, { a10: 0, ultra: 0, spark: 0 });
  }

  function giftRequirements(cart, campaign, config) {
    const counts = managedCounts(cart, campaign);
    const gifts = config && (config.products && config.products.gifts || config.gifts) || {};
    const blueUnavailable = gifts.blue && gifts.blue.available === false;
    const orangeAvailable = gifts.orange && (gifts.orange.available === true || gifts.orange.availableForSale === true);
    const alternate = counts.a10 % 2 && blueUnavailable && orangeAvailable ? 1 : 0;
    return { blue: Math.ceil(counts.a10 / 2) - alternate, orange: Math.floor(counts.a10 / 2) + alternate, sanitizer: counts.ultra, tote: counts.spark };
  }

  function giftCounts(cart, campaign) {
    return (cart.items || []).filter(item => isGift(item, campaign)).reduce((counts, item) => {
      const key = propsOf(item)._oclean_bf_gift;
      counts[key] = (counts[key] || 0) + integer(item.quantity);
      return counts;
    }, {});
  }

  function cartHasA10(cart, config) {
    const products = config.products || {};
    const variantIds = new Set(['a10', 'pair'].flatMap(key => listOf(products[key] && products[key].variants).map(variant => idOf(variant.numericId || variant.variantId || variant.id))));
    const productIds = new Set(['a10', 'pair'].map(key => idOf(products[key] && products[key].id)).filter(Boolean));
    return (cart.items || []).some(item => integer(item.quantity) > 0 && (variantIds.has(idOf(item.variant_id || item.id)) || productIds.has(idOf(item.product_id))));
  }

  function validateCart(cart, config) {
    const errors = [];
    const campaign = config.campaignId || CAMPAIGN;
    const offers = config.offers || {};
    const items = cart.items || [];
    const owned = items.filter(item => isManaged(item, campaign));
    const required = giftRequirements(cart, campaign, config);
    const actual = giftCounts(cart, campaign);
    const merchandiseGroups = new Map();
    for (const item of owned) {
      const p = propsOf(item);
      const quantity = integer(item.quantity);
      if (p._oclean_bf_mode === 'gift') {
        if (linePrice(item) !== 0) errors.push('gift_price');
        continue;
      }
      if (p._oclean_bf_mode === 'a10-addon') {
        if (!cartHasA10(cart, config)) errors.push('addon_without_a10');
        if (offers.addon && offers.addon.enabled !== true) errors.push('offer_unavailable');
        const expected = offers.addon && (offers.addon.variant && offers.addon.variant.price != null ? offers.addon.variant.price : offers.addon.expectedPrice);
        if (expected != null && linePrice(item) !== integer(expected) * quantity) errors.push('addon_price');
        continue;
      }
      const offer = offers[p._oclean_bf_offer];
      if (offer && offer.enabled !== true) errors.push('offer_unavailable');
      const target = p._oclean_bf_expected;
      if (p._oclean_bf_mode === 'merchandise' && target != null) {
        const groupKey = String(p._oclean_bf_transaction || item.key || '') + ':' + p._oclean_bf_offer;
        const group = merchandiseGroups.get(groupKey) || { expected: 0, actual: 0 };
        group.expected += integer(target) * quantity;
        group.actual += linePrice(item);
        merchandiseGroups.set(groupKey, group);
      }
    }
    for (const group of merchandiseGroups.values()) if (group.actual !== group.expected) errors.push('offer_price');
    for (const [key, quantity] of Object.entries(required)) {
      if (quantity !== (actual[key] || 0)) errors.push('gift_quantity');
    }
    const regular = owned.filter(item => propsOf(item)._oclean_bf_mode === 'regular');
    if (regular.length && offers.refills && offers.refills.enabled !== true) errors.push('offer_unavailable');
    if (regular.length && offers.refills && offers.refills.buy2get1 === true) {
      const expected = refillCalc(regular.map(item => ({ price: integer(propsOf(item)._oclean_bf_regular_price), quantity: item.quantity })));
      const paid = regular.reduce((sum, item) => sum + linePrice(item), 0);
      if (paid !== expected.net) errors.push('refill_discount');
    }
    return [...new Set(errors)];
  }

  function variantRecord(variant) {
    if (!variant) return null;
    return Object.assign({}, variant, {
      id: idOf(variant.numericId || variant.variantId || variant.id),
      available: variant.available === true || variant.availableForSale === true,
      price: integer(variant.priceCents == null ? variant.price : variant.priceCents)
    });
  }

  function variantFor(product, color) {
    if (!product) return null;
    const variants = listOf(product.variants).map(variantRecord).filter(variant => variant && variant.id);
    const defaultId = product.selectedVariantId || product.defaultVariantId || product.variantId;
    if (color == null && defaultId) return variants.find(variant => variant.id === idOf(defaultId)) || null;
    const mapped = product.selections && product.selections[color] || product.byColor && product.byColor[color];
    if (mapped) return typeof mapped === 'object' ? variantRecord(mapped) : variants.find(variant => variant.id === idOf(mapped)) || null;
    const exact = variants.find(variant => String(variant.color || '').toLowerCase() === String(color).toLowerCase());
    if (exact) return exact;
    const matches = variants.filter(variant => {
      const values = [variant.title, variant.option1, variant.option2, ...Object.values(variant.optionValues || {}), ...listOf(variant.selectedOptions).map(option => option.value)].join(' ').toLowerCase();
      if (color === 'mixed') return values.includes('gray') && values.includes('green');
      if (color === 'gray' || color === 'green') return values.includes(color) && !(values.includes('gray') && values.includes('green'));
      return true;
    });
    return matches.find(variant => variant.available) || matches[0] || (variants.length === 1 ? variants[0] : null);
  }

  function priceSummary(variant) {
    if (!variant || (variant.price == null && variant.priceCents == null)) return null;
    const price = integer(variant.priceCents == null ? variant.price : variant.priceCents);
    const compareAtPrice = integer(variant.compareAtPrice == null ? variant.compare_at_price : variant.compareAtPrice);
    return { price, compareAtPrice: compareAtPrice > price ? compareAtPrice : null, saving: Math.max(0, compareAtPrice - price) };
  }

  function offerPrice(config, name, singleColor, pairColor) {
    const products = config.products || {}, offer = (config.offers || {})[name] || {};
    if (name === 'addon') return integer(offer.variant && offer.variant.price != null ? offer.variant.price : offer.expectedPrice);
    if (name === 'pair' && offer.mode === 'singles') {
      const colors = pairColor === 'mixed' ? ['gray', 'green'] : [pairColor, pairColor];
      return colors.reduce((sum, color) => sum + integer((variantFor(products.a10, color) || {}).price), 0);
    }
    const record = variantFor(name === 'single' ? products.a10 : products[name], name === 'single' ? singleColor : name === 'pair' ? pairColor : null);
    return record ? record.price : integer(offer.expectedPrice);
  }

  const core = { priceSummary, offerPrice, refillCalc, managedCounts, giftRequirements, giftCounts, cartHasA10, validateCart, variantFor, linePrice, unitPrice, idOf };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (!global.document) return;

  const document = global.document;
  const instances = new WeakMap();

  function init(root) {
    if (!root || instances.has(root)) return;
    const embedded = root.querySelector('script[data-bf-config]');
    let config = global.OCLEAN_BF || {};
    if (embedded) {
      try { config = JSON.parse(embedded.textContent); } catch (error) { return; }
    }
    const controller = new AbortController();
    const signal = controller.signal;
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const assets = config.assets || config.A || {};
    const products = config.products || {};
    const offers = config.offers || {};
    const campaign = config.campaignId || CAMPAIGN;
    const R = Object.assign({}, config.refills || config.R || {});
    const nozzles = config.nozzles || config.NOZZLES || {};
    const strings = Object.assign({ unavailable: 'This Black Friday offer is not available yet. Please check back soon.', soldOut: 'This selection is currently unavailable. Please choose another option.', failure: "We couldn't update your bag. Please try again.", verify: 'This offer could not be applied. Please review your bag before checkout.', empty: 'Your bag is empty. Discover an A10 offer to get started.' }, config.strings || {});
    const currency = config.currency || 'USD';
    const formatMoney = cents => new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency }).format(integer(cents) / 100);
    const routeRoot = String(config.routesRoot || global.Shopify && global.Shopify.routes && global.Shopify.routes.root || '/').replace(/\/?$/, '/');
    const refs = Object.fromEntries(Object.keys(R).map(key => [key, 0]));
    const selections = new Map();
    const observers = [];
    let cart = null;
    let pending = Promise.resolve();
    let busy = false;
    let singleColor = 'gray';
    let pairColor = 'mixed';
    let lastFocus = null;
    let oldBodyOverflow = '';
    let toastTimer;
    let statusMessage = '';

    for (const product of Object.values(nozzles)) {
      for (const value of listOf(product.variants)) {
        const key = value.key || 'n_' + idOf(value.numericId || value.id);
        value.key = key;
        if (!R[key]) R[key] = Object.assign({}, value, { variantId: value.numericId || value.id, name: product.name, img: value.img, price: value.price == null ? value.priceCents : value.price });
        if (!(key in refs)) refs[key] = 0;
      }
    }

    function on(element, event, handler, options) {
      if (element) element.addEventListener(event, handler, Object.assign({ signal }, options));
    }

    function imageURL(value) { return assets[value] || value || ''; }
    function rowProperties(offer, mode, extra) {
      return Object.assign({ _oclean_bf_campaign: campaign, _oclean_bf_offer: offer, _oclean_bf_mode: mode }, extra || {});
    }

    function message(text, button) {
      statusMessage = text || '';
      if ($('#cart-status')) $('#cart-status').textContent = statusMessage;
      if (button) {
        let target = button.parentElement.querySelector('[data-bf-message]');
        if (!target) {
          target = document.createElement('p');
          target.className = 'small bf-status';
          target.dataset.bfMessage = '';
          target.setAttribute('role', 'status');
          button.insertAdjacentElement('afterend', target);
        }
        target.textContent = text || '';
      }
      const toast = $('#toast');
      if (toast && text) {
        clearTimeout(toastTimer);
        toast.textContent = text;
        toast.hidden = false;
        toastTimer = setTimeout(() => { toast.hidden = true; }, 5500);
      }
    }

    function setBusy(value) {
      busy = value;
      root.setAttribute('aria-busy', String(value));
      $$('#add-single,#add-pair,[data-cart],#add-refills,[data-remove],[data-cart-step],#bf-checkout').forEach(button => {
        if (value) {
          button.dataset.bfDisabled = button.disabled ? 'true' : 'false';
          button.disabled = true;
        } else {
          button.disabled = button.dataset.bfDisabled === 'true';
          delete button.dataset.bfDisabled;
        }
      });
      if (!value) {
        renderRefills();
        renderCheckout();
      }
    }

    async function request(endpoint, body) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 20000);
      try {
        const response = await fetch(routeRoot + endpoint, { method: body == null ? 'GET' : 'POST', credentials: 'same-origin', headers: Object.assign({ Accept: 'application/json' }, body == null ? {} : { 'Content-Type': 'application/json' }), body: body == null ? undefined : JSON.stringify(body), signal: abort.signal, cache: 'no-store' });
        let payload;
        try { payload = await response.json(); } catch (error) { throw new Error(strings.failure); }
        if (!response.ok || payload.status && payload.status >= 400) throw new Error(payload.description || payload.message || strings.failure);
        return payload;
      } catch (error) {
        throw new Error(error.name === 'AbortError' ? strings.failure : error.message || strings.failure);
      } finally { clearTimeout(timeout); }
    }

    async function refreshCart() {
      cart = await request('cart.js');
      renderCart();
      return cart;
    }

    function enqueue(task, button) {
      pending = pending.catch(() => {}).then(async () => {
        setBusy(true);
        try { await task(); } catch (error) {
          message(error.message || strings.failure, button);
          try { await refreshCart(); } catch (ignored) {}
        } finally { setBusy(false); }
      });
      return pending;
    }

    function ensureOffer(name) {
      const offer = offers[name];
      if (!offer || offer.enabled !== true) throw new Error(offer && offer.message || strings.unavailable);
      return offer;
    }

    function ensureVariant(variant) {
      if (!variant || !idOf(variant.id || variant.variantId || variant.numericId) || variant.available === false || variant.availableForSale === false) throw new Error(strings.soldOut);
      return variant;
    }

    function giftData(key) {
      const gift = products.gifts && products.gifts[key] || config.gifts && config.gifts[key];
      if (!gift || !(gift.available === true || gift.availableForSale === true)) throw new Error(strings.soldOut);
      const id = idOf(gift.variantId || gift.id || gift.numericId);
      if (!id) throw new Error(strings.unavailable);
      return Object.assign({}, gift, { id });
    }

    function addMissingGifts(plannedItems, current, transaction) {
      const next = { items: (current.items || []).concat(plannedItems.map(item => ({ quantity: item.quantity, properties: item.properties }))) };
      const wanted = giftRequirements(next, campaign, config);
      const existing = giftCounts(current, campaign);
      const additions = [];
      for (const [key, quantity] of Object.entries(wanted)) {
        const needed = quantity - (existing[key] || 0);
        if (needed <= 0) continue;
        const gift = giftData(key);
        if (gift.inventoryQuantity != null && Number(gift.inventoryQuantity) < needed) throw new Error(strings.soldOut);
        additions.push({ id: gift.id, quantity: needed, properties: rowProperties('gift', 'gift', { _oclean_bf_gift: key, _oclean_bf_transaction: transaction }) });
      }
      return additions;
    }

    async function rollbackTransaction(transaction) {
      let latest = await request('cart.js');
      const additions = latest.items.filter(item => isManaged(item, campaign) && propsOf(item)._oclean_bf_transaction === transaction);
      for (const item of additions) latest = await request('cart/change.js', { id: item.key, quantity: 0 });
      cart = latest;
      renderCart();
    }

    async function addItems(items, transaction) {
      const previous = await request('cart.js');
      const gifts = addMissingGifts(items, previous, transaction);
      const all = items.concat(gifts);
      try {
        await request('cart/add.js', { items: all });
        cart = await request('cart.js');
        const errors = validateCart(cart, config);
        if (errors.length) throw new Error(strings.verify);
      } catch (error) {
        try { await rollbackTransaction(transaction); } catch (ignored) {}
        throw error;
      }
      statusMessage = '';
      renderCart();
      openCart(false);
    }

    function transactionId() { return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10); }

    function quotePrice(name) {
      return offerPrice(config, name, singleColor, pairColor);
    }

    async function addMain(name) {
      const offer = ensureOffer(name);
      const transaction = transactionId();
      let items = [];
      if (name === 'pair' && offer.mode === 'singles') {
        const colors = pairColor === 'mixed' ? ['gray', 'green'] : [pairColor, pairColor];
        const variants = colors.map(color => ensureVariant(variantFor(products.a10, color)));
        if (!offer.allowDiscount && variants.reduce((sum, variant) => sum + variant.price, 0) !== quotePrice('pair')) throw new Error(strings.unavailable);
        const unitTarget = quotePrice('pair') / 2;
        if (!Number.isInteger(unitTarget)) {
          for (let index = 0; index < variants.length; index++) {
            items.push({ id: variants[index].id, quantity: 1, properties: rowProperties('pair', 'merchandise', { _oclean_bf_units: '1', _oclean_bf_expected: String(index === 0 ? Math.ceil(unitTarget) : Math.floor(unitTarget)), _oclean_bf_transaction: transaction, _oclean_bf_pair_slot: String(index) }) });
          }
        } else {
          items = variants.map((variant, index) => ({ id: variant.id, quantity: 1, properties: rowProperties('pair', 'merchandise', { _oclean_bf_units: '1', _oclean_bf_expected: String(unitTarget), _oclean_bf_transaction: transaction, _oclean_bf_pair_slot: String(index) }) }));
        }
      } else {
        const source = name === 'single' ? products.a10 : products[name];
        const variant = ensureVariant(variantFor(source, name === 'single' ? singleColor : name === 'pair' ? pairColor : null));
        if (!offer.allowDiscount && variant.price !== quotePrice(name)) throw new Error(strings.unavailable);
        items.push({ id: variant.id, quantity: 1, properties: rowProperties(name, 'merchandise', { _oclean_bf_units: name === 'pair' ? '2' : '1', _oclean_bf_expected: String(quotePrice(name)), _oclean_bf_transaction: transaction }) });
      }
      if ((name === 'single' || name === 'pair') && $('#' + name + '-addon') && $('#' + name + '-addon').checked) {
        const addon = ensureOffer('addon');
        const variant = ensureVariant(addon.variant || { id: addon.variantId, available: addon.available });
        items.push({ id: idOf(variant.id || variant.variantId), quantity: 1, properties: rowProperties('addon', 'a10-addon', { _oclean_bf_refill_key: addon.refillKey || '', _oclean_bf_expected: String(quotePrice('addon')), _oclean_bf_transaction: transaction }) });
      }
      await addItems(items, transaction);
    }

    async function addRefills() {
      const offer = ensureOffer('refills');
      const transaction = transactionId();
      const items = Object.entries(refs).filter(([, quantity]) => quantity > 0).map(([key, quantity]) => {
        const item = ensureVariant(R[key]);
        return { id: idOf(item.variantId || item.numericId || item.id), quantity, properties: rowProperties('refills', 'regular', { _oclean_bf_refill_key: key, _oclean_bf_regular_price: String(integer(item.price)), _oclean_bf_transaction: transaction }) };
      });
      if (!items.length) return;
      if (offer.buy2get1 !== true && selectedRefills().some(item => integer(item.price) !== integer(item.actualPrice == null ? item.price : item.actualPrice))) throw new Error(strings.unavailable);
      await addItems(items, transaction);
      for (const key of Object.keys(refs)) refs[key] = 0;
      renderRefills();
    }

    async function reconcileManagedCart() {
      let latest = await request('cart.js');
      if (!cartHasA10(latest, config)) {
        const addons = latest.items.filter(item => isManaged(item, campaign) && propsOf(item)._oclean_bf_mode === 'a10-addon');
        for (const addon of addons) {
          const p = propsOf(addon);
          const regular = R[p._oclean_bf_refill_key];
          if (!regular || !offers.refills || offers.refills.enabled !== true) {
            latest = await request('cart/change.js', { id: addon.key, quantity: 0 });
            statusMessage = 'The A10 add-on was removed because there is no A10 in your bag.';
          } else {
            const properties = Object.assign({}, p, { _oclean_bf_offer: 'refills', _oclean_bf_mode: 'regular', _oclean_bf_regular_price: String(integer(regular.price)) });
            delete properties._oclean_bf_expected;
            latest = await request('cart/change.js', { id: addon.key, quantity: addon.quantity, properties });
            statusMessage = 'A10 removed: nozzle packs now use the regular purchase offer.';
          }
        }
      }
      const needed = giftRequirements(latest, campaign, config);
      const remaining = Object.assign({}, needed);
      for (const item of latest.items.filter(value => isGift(value, campaign))) {
        const key = propsOf(item)._oclean_bf_gift;
        const keep = Math.min(integer(item.quantity), remaining[key] || 0);
        remaining[key] = Math.max(0, (remaining[key] || 0) - keep);
        if (keep !== item.quantity) latest = await request('cart/change.js', { id: item.key, quantity: keep });
      }
      const transaction = transactionId();
      const missing = [];
      for (const [key, quantity] of Object.entries(remaining)) {
        if (quantity <= 0) continue;
        const gift = giftData(key);
        missing.push({ id: gift.id, quantity, properties: rowProperties('gift', 'gift', { _oclean_bf_gift: key, _oclean_bf_transaction: transaction }) });
      }
      if (missing.length) {
        await request('cart/add.js', { items: missing });
        latest = await request('cart.js');
        if (latest.items.some(item => propsOf(item)._oclean_bf_transaction === transaction && isGift(item, campaign) && linePrice(item) !== 0)) {
          await rollbackTransaction(transaction);
          throw new Error(strings.verify);
        }
      }
      cart = latest;
      renderCart();
    }

    async function changeLine(key, quantity) {
      const current = await request('cart.js');
      const item = current.items.find(line => line.key === key);
      if (!item) { cart = current; renderCart(); return; }
      if (isGift(item, campaign)) throw new Error('Gift quantities follow the qualifying products in your bag.');
      const nextQuantity = Math.max(0, Math.min(isManaged(item, campaign) ? LIMIT : 9999, integer(quantity)));
      const p = propsOf(item);
      if (p._oclean_bf_offer === 'pair' && offers.pair && offers.pair.mode === 'singles') {
        const members = current.items.filter(line => isManaged(line, campaign) && propsOf(line)._oclean_bf_offer === 'pair' && propsOf(line)._oclean_bf_transaction === p._oclean_bf_transaction);
        for (const member of members) await request('cart/change.js', { id: member.key, quantity: nextQuantity });
      } else await request('cart/change.js', { id: key, quantity: nextQuantity });
      await reconcileManagedCart();
      if (validateCart(cart, config).length) message(strings.verify);
    }

    function shippingView() {
      const target = $('#shipping-status');
      if (!target) return;
      const threshold = integer(config.shippingThreshold == null ? 2000 : config.shippingThreshold);
      const total = cart ? integer(cart.total_price) : 0;
      const count = cart ? integer(cart.item_count) : 0;
      const unlocked = count > 0 && total >= threshold;
      const messageText = !count ? 'Free US shipping on orders ' + formatMoney(threshold).replace(/\.00$/, '') + '+' : unlocked ? 'FREE US shipping unlocked' : 'Add ' + formatMoney(threshold - total) + ' for FREE US shipping';
      target.classList.add('shipping-threshold');
      target.dataset.state = !count ? 'empty' : unlocked ? 'unlocked' : 'pending';
      target.setAttribute('aria-atomic', 'true');
      target.innerHTML = '<p class="shipping-message">' + escapeHTML(messageText) + '</p><div class="shipping-progress" aria-hidden="true"><span class="shipping-progress-fill" style="width:' + (count ? Math.min(100, total / Math.max(1, threshold) * 100) : 0) + '%"></span></div>' + (count ? '<div class="shipping-row"><span>US shipping</span><span class="shipping-rate">' + (unlocked ? 'FREE' : 'Calculated at checkout') + '</span></div>' : '');
    }

    function cartRow(item) {
      const p = propsOf(item);
      const gift = isGift(item, campaign) && linePrice(item) === 0;
      const photo = item.image || item.featured_image && item.featured_image.url || '';
      const title = item.product_title || item.title || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const price = linePrice(item);
      if (gift) {
        const key = p._oclean_bf_gift;
        const details = products.gifts && products.gifts[key] || config.gifts && config.gifts[key] || {};
        const giftValue = details.price == null ? details.giftValue : details.price;
        const value = giftValue == null ? '' : '<span class="gift-value">' + formatMoney(giftValue) + ' gift value</span>';
        return '<div class="cart-gift">' + (photo ? '<img src="' + escapeHTML(photo) + '" alt="' + escapeHTML(title) + '">' : '') + '<span><strong class="gift-name">' + escapeHTML(title) + ' × ' + integer(item.quantity) + '</strong>' + value + '<span class="gift-detail">While supplies last.</span></span><b>FREE</b></div>';
      }
      const mode = p._oclean_bf_mode === 'a10-addon' ? 'A10 add-on · Not in Buy 2 Get 1' : p._oclean_bf_mode === 'regular' ? 'Regular purchase · Buy 2 Get 1 eligible' : '';
      const discounts = (item.line_level_discount_allocations || item.discount_allocations || []).map(allocation => '<span class="cart-mode">' + escapeHTML(allocation.discount_application && allocation.discount_application.title || 'Discount') + ' −' + formatMoney(allocation.amount) + '</span>').join('');
      return '<div class="cart-row">' + (photo ? '<img src="' + escapeHTML(photo) + '" alt="' + escapeHTML(title) + '">' : '<span></span>') + '<div><h3>' + escapeHTML(title) + '</h3><p>' + escapeHTML(variant) + '</p>' + (mode ? '<span class="cart-mode">' + mode + '</span>' : '') + discounts + '<div class="cart-pack-qty"><button type="button" data-cart-step="-1" data-cart-key="' + escapeHTML(item.key) + '" aria-label="Remove one ' + escapeHTML(title) + '">−</button><span>' + integer(item.quantity) + '</span><button type="button" data-cart-step="1" data-cart-key="' + escapeHTML(item.key) + '" aria-label="Add one ' + escapeHTML(title) + '"' + (isManaged(item, campaign) && item.quantity >= LIMIT ? ' disabled' : '') + '>+</button><small>' + formatMoney(unitPrice(item)) + ' each</small></div><button type="button" class="remove" data-remove="' + escapeHTML(item.key) + '" aria-label="Remove ' + escapeHTML(title) + '">Remove</button></div><strong>' + (item.original_line_price > price ? '<del>' + formatMoney(item.original_line_price) + '</del> ' : '') + formatMoney(price) + '</strong></div>';
    }

    function renderCheckout() {
      const checkout = $('#bf-checkout');
      if (!checkout) return;
      const invalid = cart && validateCart(cart, config).length > 0;
      checkout.disabled = busy || !cart || !cart.item_count || invalid;
      checkout.setAttribute('aria-disabled', String(checkout.disabled));
      if (invalid && $('#cart-status') && !statusMessage) $('#cart-status').textContent = strings.verify;
    }

    function renderCart() {
      const count = cart ? integer(cart.item_count) : 0;
      const content = $('#cart-content');
      if (content) {
        const items = cart ? cart.items || [] : [];
        const paid = items.filter(item => !isGift(item, campaign) || linePrice(item) !== 0);
        const gifts = items.filter(item => isGift(item, campaign) && linePrice(item) === 0);
        content.innerHTML = !cart ? '<div class="empty-cart">Loading your bag…</div>' : !items.length ? '<div class="empty-cart">' + escapeHTML(strings.empty) + '</div>' : paid.concat(gifts).map(cartRow).join('');
        if (cart && cart.total_discount > 0) content.insertAdjacentHTML('beforeend', '<div class="cart-gift"><span>Discounts applied</span><b>−' + formatMoney(cart.total_discount) + '</b></div>');
      }
      if ($('#subtotal')) $('#subtotal').textContent = cart ? formatMoney(cart.total_price) : '…';
      if ($('#cart-count')) $('#cart-count').textContent = count;
      if ($('#cart-status')) $('#cart-status').textContent = statusMessage;
      document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; });
      shippingView();
      renderCheckout();
      if (cart) {
        document.dispatchEvent(new CustomEvent('oclean:cart:updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      }
    }

    function openCart(shouldRefresh) {
      const drawer = $('#drawer');
      if (!drawer) return;
      if (!drawer.classList.contains('open')) {
        lastFocus = document.activeElement;
        oldBodyOverflow = document.body.style.overflow;
      }
      drawer.inert = false;
      drawer.classList.add('open');
      if ($('#overlay')) $('#overlay').classList.add('open');
      document.body.style.overflow = 'hidden';
      if ($('#close-cart')) $('#close-cart').focus();
      if (shouldRefresh !== false) enqueue(refreshCart);
    }

    function closeCart() {
      const drawer = $('#drawer');
      if (!drawer || !drawer.classList.contains('open')) return;
      drawer.classList.remove('open');
      drawer.inert = true;
      if ($('#overlay')) $('#overlay').classList.remove('open');
      document.body.style.overflow = oldBodyOverflow;
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    }

    function selectedRefills() { return Object.entries(refs).filter(([, quantity]) => quantity > 0).map(([key, quantity]) => Object.assign({}, R[key], { key, quantity })); }

    function renderRefills() {
      for (const [key, quantity] of Object.entries(refs)) if ($('#qty-' + key)) $('#qty-' + key).textContent = quantity;
      const selected = selectedRefills();
      const estimate = refillCalc(selected);
      const remainder = estimate.quantity % 3;
      $$('.progress span').forEach((node, index) => node.classList.toggle('on', estimate.quantity > 0 && (remainder === 0 || index < remainder)));
      if ($('#refill-progress')) $('#refill-progress').textContent = !estimate.quantity ? 'Choose 3 eligible packs. The lowest-priced pack is free.' : estimate.free ? 'Estimated saving ' + formatMoney(estimate.discount) + '. Your offer is confirmed in your bag.' : 'Add ' + (3 - estimate.quantity) + ' more eligible pack' + (estimate.quantity === 2 ? '' : 's') + ' to unlock your free pack.';
      if ($('#refill-amount')) {
        $('#refill-amount').innerHTML = (estimate.discount ? '<del>' + formatMoney(estimate.total) + '</del>' : '') + '<strong>' + formatMoney(estimate.net) + '</strong>';
        $('#refill-amount').setAttribute('aria-label', 'Estimated total ' + formatMoney(estimate.net));
      }
      if ($('#add-refills')) $('#add-refills').disabled = busy || estimate.quantity === 0;
      $$('[data-refill]').forEach(button => {
        const item = R[button.dataset.refill] || {};
        const quantity = integer(refs[button.dataset.refill]);
        button.disabled = Number(button.dataset.step) < 0 ? quantity === 0 : item.available === false || quantity >= LIMIT;
      });
      renderNozzles();
    }

    function selectedNozzle(key) {
      const product = nozzles[key];
      const variants = listOf(product && product.variants);
      return variants.find(value => value.key === selections.get(key)) || variants.find(value => value.key === product.initialKey) || variants.find(value => value.available !== false) || variants[0];
    }

    function optionValue(variant, axis) {
      if (variant.optionValues && variant.optionValues[axis] != null) return String(variant.optionValues[axis]);
      return String((listOf(variant.selectedOptions).find(option => option.name === axis) || {}).value || '');
    }

    function renderNozzles() {
      $$('.nozzle-card[data-nozzle-product]').forEach(card => {
        const key = card.dataset.nozzleProduct;
        const product = nozzles[key];
        const variant = selectedNozzle(key);
        if (!product || !variant) return;
        selections.set(key, variant.key);
        const $c = selector => card.querySelector(selector);
        const primary = $c('[data-nozzle-field="primary"]');
        const size = $c('[data-nozzle-field="size"]');
        if (primary) primary.value = optionValue(variant, card.dataset.nozzlePrimaryAxis);
        if (size) size.value = optionValue(variant, 'Size');
        const image = $c('[data-nozzle-image]');
        const src = imageURL(variant.img || R[variant.key] && R[variant.key].img);
        if (image && src) { image.src = src; image.alt = (product.name || key.toUpperCase() + ' nozzles') + ' · ' + variant.title; }
        if ($c('[data-nozzle-selection]')) $c('[data-nozzle-selection]').textContent = String(variant.title).replace(/\s*\/\s*/g, ' · ');
        if ($c('[data-nozzle-price-wrap]')) $c('[data-nozzle-price-wrap]').innerHTML = priceMarkup(R[variant.key] || variant, true);
        if ($c('[data-nozzle-quantity]')) $c('[data-nozzle-quantity]').textContent = integer(refs[variant.key]);
        if ($c('[data-nozzle-availability]')) $c('[data-nozzle-availability]').hidden = variant.available !== false;
        card.querySelectorAll('[data-nozzle-step]').forEach(button => {
          button.disabled = Number(button.dataset.nozzleStep) < 0 ? !refs[variant.key] : variant.available === false || integer(refs[variant.key]) >= LIMIT;
          button.setAttribute('aria-label', (Number(button.dataset.nozzleStep) < 0 ? 'Remove' : 'Add') + ' one ' + key.toUpperCase() + ' ' + variant.title + ' nozzle pack');
        });
        const other = listOf(product.variants).reduce((sum, value) => sum + integer(refs[value.key]), 0) - integer(refs[variant.key]);
        if ($c('[data-nozzle-total]')) { $c('[data-nozzle-total]').hidden = other === 0; $c('[data-nozzle-total]').textContent = other + ' pack' + (other === 1 ? '' : 's') + ' selected in other options.'; }
      });
    }

    function priceMarkup(record, unit) {
      const summary = priceSummary(record);
      if (!summary) return '';
      return escapeHTML(formatMoney(summary.price)) + (summary.saving ? '<del>' + escapeHTML(formatMoney(summary.compareAtPrice)) + '</del><span class="offer-save">Save ' + escapeHTML(formatMoney(summary.saving).replace(/([.,])00$/, '')) + '</span>' : '') + (unit ? ' <span class="small">/ pack</span>' : '');
    }

    function renderCatalogPrices() {
      $$('[data-bf-price]').forEach(node => {
        const key = node.dataset.bfPrice;
        const record = variantFor(key === 'single' ? products.a10 : products[key], key === 'single' ? singleColor : key === 'pair' ? pairColor : null);
        node.innerHTML = priceMarkup(record, false);
      });
      $$('[data-bf-refill-price]').forEach(node => { const record = R[node.dataset.bfRefillPrice]; node.innerHTML = priceMarkup(record && idOf(record.id || record.variantId) ? record : null, true); });
      $$('[data-bf-gift-keys]').forEach(node => {
        const gifts = products.gifts || {};
        const records = node.dataset.bfGiftKeys.split(',').map(key => gifts[key]);
        node.hidden = records.some(record => !record || !idOf(record.id || record.variantId) || (record.price == null && record.giftValue == null));
        if (node.hidden) return;
        const value = records.reduce((sum, record) => sum + integer(record.price == null ? record.giftValue : record.price), 0);
        node.textContent = formatMoney(value) + ' gift value';
      });
      $$('[data-bf-addon-price]').forEach(node => { node.textContent = offers.addon && offers.addon.variant && !idOf(offers.addon.variant.id || offers.addon.variant.variantId) ? '—' : '+' + formatMoney(quotePrice('addon')); });
      if ($('[data-bf-mobile-price]')) $('[data-bf-mobile-price]').textContent = 'A10 · ' + (variantFor(products.a10, singleColor) ? formatMoney(quotePrice('single')) : '—');
      updateButtonPrice('single');
      updateButtonPrice('pair');
    }

    function setColor(kind, color) {
      if (kind === 'single') singleColor = color; else pairColor = color;
      $$('[data-' + kind + ']').forEach(button => button.setAttribute('aria-pressed', String(button.dataset[kind] === color)));
      if (kind === 'single' && $('#single-image')) { $('#single-image').src = imageURL(color === 'gray' ? 'a10' : 'mint'); $('#single-image').alt = 'A10 in ' + (color === 'gray' ? 'Gray' : 'Green'); }
      if (kind === 'pair') {
        if ($('#pair-first')) { $('#pair-first').src = imageURL(color === 'green' ? 'mint' : 'a10'); $('#pair-first').alt = 'First A10 in ' + (color === 'green' ? 'Green' : 'Gray'); }
        if ($('#pair-second')) { $('#pair-second').src = imageURL(color === 'gray' ? 'a10' : 'mint'); $('#pair-second').alt = 'Second A10 in ' + (color === 'gray' ? 'Gray' : 'Green'); }
      }
      renderCatalogPrices();
    }

    function updateButtonPrice(kind) {
      const record = variantFor(kind === 'single' ? products.a10 : products.pair, kind === 'single' ? singleColor : pairColor);
      if ($('#' + kind + '-button-price')) $('#' + kind + '-button-price').textContent = record ? formatMoney(quotePrice(kind) + ($('#' + kind + '-addon') && $('#' + kind + '-addon').checked ? quotePrice('addon') : 0)) : '—';
    }

    function shelf(trackSelector, previousSelector, nextSelector, statusSelector, paged) {
      const track = $(trackSelector), previous = $(previousSelector), next = $(nextSelector), status = $(statusSelector);
      if (!track || !previous || !next || !status) return () => {};
      function measures() {
        const items = [...track.querySelectorAll(paged ? '.refill-page' : '.regular-card')];
        const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth;
        return { items, step: Math.max(1, step), max: Math.max(0, track.scrollWidth - track.clientWidth), offset: Math.max(0, track.scrollLeft) };
      }
      function update() {
        const m = measures();
        previous.disabled = m.offset < 3;
        next.disabled = m.offset >= m.max - 3;
        if (paged) status.textContent = String(Math.round(m.offset / m.step) + 1).padStart(2, '0') + ' / ' + String(m.items.length).padStart(2, '0');
        else { const start = Math.round(m.offset / m.step), visible = Math.max(1, Math.round((track.clientWidth + 20) / m.step)); status.textContent = (start + 1) + '–' + Math.min(start + visible, m.items.length) + ' of ' + m.items.length; }
      }
      function move(direction) { const m = measures(); track.scrollBy({ left: direction * m.step * (paged ? 1 : Math.max(1, Math.round((track.clientWidth + 20) / m.step))), behavior: global.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); }
      on(previous, 'click', () => move(-1)); on(next, 'click', () => move(1)); on(track, 'scroll', update, { passive: true }); on(global, 'resize', update);
      on(track, 'keydown', event => { if (event.target !== track) return; if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); } });
      if (global.ResizeObserver) { const observer = new ResizeObserver(update); observer.observe(track); observers.push(observer); }
      update();
      return update;
    }

    on(root, 'click', event => {
      const button = event.target.closest('button,a');
      if (!button || !root.contains(button)) return;
      if (button.matches('[data-single]')) setColor('single', button.dataset.single);
      else if (button.matches('[data-pair]')) setColor('pair', button.dataset.pair);
      else if (button.id === 'add-single' || button.id === 'add-pair') { event.preventDefault(); enqueue(() => addMain(button.id === 'add-pair' ? 'pair' : 'single'), button); }
      else if (button.matches('[data-cart]')) { event.preventDefault(); enqueue(() => addMain(button.dataset.cart), button); }
      else if (button.id === 'add-refills') { event.preventDefault(); enqueue(addRefills, button); }
      else if (button.matches('[data-refill]')) { const key = button.dataset.refill; if (key in refs) { refs[key] = Math.max(0, Math.min(LIMIT, integer(refs[key]) + Number(button.dataset.step))); renderRefills(); } }
      else if (button.matches('[data-nozzle-step]')) { const card = button.closest('[data-nozzle-product]'), variant = selectedNozzle(card.dataset.nozzleProduct); if (variant) { refs[variant.key] = Math.max(0, Math.min(LIMIT, integer(refs[variant.key]) + Number(button.dataset.nozzleStep))); renderRefills(); } }
      else if (button.id === 'open-cart') { event.preventDefault(); openCart(true); }
      else if (button.id === 'close-cart' || button.id === 'continue-shopping') { event.preventDefault(); closeCart(); }
      else if (button.matches('[data-remove]')) { event.preventDefault(); enqueue(() => changeLine(button.dataset.remove, 0)); }
      else if (button.matches('[data-cart-step]')) { event.preventDefault(); const item = cart && cart.items.find(line => line.key === button.dataset.cartKey); if (item) enqueue(() => changeLine(item.key, integer(item.quantity) + Number(button.dataset.cartStep))); }
      else if (button.id === 'bf-checkout') { event.preventDefault(); enqueue(async () => { await refreshCart(); if (!cart.item_count || validateCart(cart, config).length) throw new Error(strings.verify); global.location.assign(routeRoot + 'checkout'); }, button); }
      else if (button.matches('.menu-toggle')) { const nav = $('#main-nav'); if (nav) button.setAttribute('aria-expanded', String(nav.classList.toggle('open'))); }
      else if (button.id === 'explore-more') { const track = $('#deals-track'); if (track) { const expanded = track.classList.toggle('expanded'); button.setAttribute('aria-expanded', String(expanded)); if (button.querySelector('.toggle-label')) button.querySelector('.toggle-label').textContent = expanded ? 'Show less' : 'Explore more'; if (button.querySelector('.toggle-symbol')) button.querySelector('.toggle-symbol').textContent = expanded ? '−' : '+'; } }
      if (button.matches('#main-nav a')) { $('#main-nav').classList.remove('open'); if ($('.menu-toggle')) $('.menu-toggle').setAttribute('aria-expanded', 'false'); }
    });

    on(root, 'change', event => {
      if (event.target.id === 'single-addon') updateButtonPrice('single');
      if (event.target.id === 'pair-addon') updateButtonPrice('pair');
      if (!event.target.matches('[data-nozzle-field]')) return;
      const card = event.target.closest('[data-nozzle-product]');
      if (!card) return;
      const product = nozzles[card.dataset.nozzleProduct];
      if (!product) return;
      const axis = card.dataset.nozzlePrimaryAxis;
      const primary = card.querySelector('[data-nozzle-field="primary"]').value;
      const size = card.querySelector('[data-nozzle-field="size"]').value;
      const variants = listOf(product.variants);
      const variant = variants.find(value => optionValue(value, axis) === primary && optionValue(value, 'Size') === size) || variants.find(value => optionValue(value, axis) === primary && value.available !== false) || variants.find(value => optionValue(value, axis) === primary);
      if (variant) selections.set(card.dataset.nozzleProduct, variant.key);
      renderNozzles();
    });

    on($('#overlay'), 'click', closeCart);
    on(document, 'keydown', event => {
      const drawer = $('#drawer');
      if (!drawer || !drawer.classList.contains('open')) return;
      if (event.key === 'Escape') closeCart();
      if (event.key !== 'Tab') return;
      const focusables = [...drawer.querySelectorAll('button:not([disabled]),a[href],input:not([disabled])')].filter(node => node.offsetParent !== null);
      if (!focusables.length) return;
      if (event.shiftKey && document.activeElement === focusables[0]) { event.preventDefault(); focusables[focusables.length - 1].focus(); }
      else if (!event.shiftKey && document.activeElement === focusables[focusables.length - 1]) { event.preventDefault(); focusables[0].focus(); }
    });

    shelf('#deals-track', '#deals-prev', '#deals-next', '#deals-status', false);
    shelf('#refill-viewport', '#refill-prev', '#refill-next', '#refill-status', true);
    if (global.IntersectionObserver) {
      const sectionObserver = new IntersectionObserver(entries => { for (const entry of entries) if (entry.isIntersecting) $$('.anchors a').forEach(link => link.classList.toggle('active', link.getAttribute('href') === '#' + entry.target.id)); }, { rootMargin: '-15% 0px -60% 0px' });
      ['a10', 'gifts', 'more', 'refills'].forEach(id => { if ($('#' + id)) sectionObserver.observe($('#' + id)); });
      observers.push(sectionObserver);
      if ($('.hero') && $('#mobile-sticky')) { const observer = new IntersectionObserver(entries => $('#mobile-sticky').classList.toggle('visible', !entries[0].isIntersecting), { threshold: 0 }); observer.observe($('.hero')); observers.push(observer); }
    }
    on(global, 'pageshow', event => { if (event.persisted) enqueue(refreshCart); });
    on(document, 'visibilitychange', () => { if (document.visibilityState === 'visible' && $('#drawer') && $('#drawer').classList.contains('open')) enqueue(refreshCart); });

    const instance = { refresh: () => enqueue(refreshCart), open: () => openCart(true), close: closeCart, getCart: () => cart, destroy() { controller.abort(); observers.forEach(observer => observer.disconnect()); clearTimeout(toastTimer); closeCart(); instances.delete(root); } };
    instances.set(root, instance);
    global.OcleanBFCommerce = Object.assign({}, core, instance);
    renderRefills();
    renderCart();
    renderCatalogPrices();
    enqueue(refreshCart);
  }

  function boot() {
    const config = global.OCLEAN_BF || {};
    const root = document.querySelector(config.root || '[data-oclean-bf]') || document.getElementById('oclean-bf-2026');
    if (root) init(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', event => { const root = event.target.matches('[data-oclean-bf]') ? event.target : event.target.querySelector('[data-oclean-bf]'); if (root && instances.has(root)) instances.get(root).destroy(); });
})(typeof window === 'undefined' ? globalThis : window);
